import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import { OnboardingUser } from '../services/HxcStagingService';
import { logger } from 'utils/logger';

const log = logger.getLogger('DeleteOnboardingDialog');

interface DeleteOnboardingDialogProps {
  open: boolean;
  onClose: () => void;
  user: OnboardingUser | null;
  onDelete: (userId: number) => Promise<void>;
}

type DialogState = 'confirmation' | 'processing' | 'error';

const DeleteOnboardingDialog: React.FC<DeleteOnboardingDialogProps> = ({
  open,
  user,
  onClose,
  onDelete
}) => {
  const [dialogState, setDialogState] = useState<DialogState>('confirmation');
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setDialogState('confirmation');
      setError(null);
    }
  }, [open]);

  const handleCancel = () => {
    if (dialogState !== 'processing') {
      onClose();
    }
  };

  const handleClose = () => {
    onClose();
  };

  const handleDelete = async () => {
    if (!user) return;
    
    try {
      setDialogState('processing');
      setError(null);
      
      log.log(`Deleting onboarding user with ID: ${user.id}`);
      
      await onDelete(user.id);
      
      log.log(`Successfully deleted onboarding user: ${user.username}`);
      
      // Close dialog immediately on success
      onClose();
    } catch (err: any) {
      log.error('Error deleting onboarding user:', err);
      setError(err.message || 'Failed to delete user. Please try again.');
      setDialogState('error');
    }
  };

  if (!user) return null;

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
    >
      {dialogState === 'confirmation' && (
        <>
          <DialogTitle>Delete Onboarding Entry</DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              If you delete this entry, the welcome email will need to be sent to this user manually. This will not remove the user from the Hx system.
            </Typography>
            <br></br>
            <Typography variant="body1" gutterBottom>
              Are you sure you want to remove <strong>{user.fullname}</strong> ({user.username}) from onboarding?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button 
              onClick={handleDelete} 
              variant="contained" 
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </>
      )}

      {dialogState === 'processing' && (
        <>
          <DialogTitle>Deleting Entry</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="body1" sx={{ mt: 3 }}>
                Deleting onboarding entry...
              </Typography>
            </Box>
          </DialogContent>
        </>
      )}

      {dialogState === 'error' && (
        <>
          <DialogTitle>Error Deleting Entry</DialogTitle>
          <DialogContent>
            <Box sx={{ py: 2 }}>
              <Typography variant="body1" color="error.main" gutterBottom>
                ❌ Failed to delete entry
              </Typography>
              {error && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  {error}
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} variant="contained">
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default DeleteOnboardingDialog;
